import React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

// Reusable neo-brutalist button style for custom <button> elements (3px shadow, sm contexts).
// Usage: className={cn(neoBtnSm, "your-layout-classes")}
export const neoBtnSm =
  "bg-plantista-400 dark:bg-plantista-400 text-plantista-950 border-2 border-plantista-950 shadow-[3px_3px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 hover:bg-plantista-500 dark:hover:bg-plantista-300 active:translate-y-[1px] active:translate-x-[1px] active:shadow-[1px_1px_0px_0px] transition-all";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-plantista-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-plantista-950 dark:focus-visible:ring-plantista-300",
  {
    variants: {
      variant: {
        default:
          "bg-plantista-400 dark:bg-plantista-400 text-plantista-950 hover:bg-plantista-500 dark:hover:bg-plantista-300 shadow-[4px_4px_0px_0px] shadow-plantista-950 dark:shadow-plantista-50 border-2 border-plantista-950 active:translate-y-[2px] active:translate-x-[2px] active:shadow-[2px_2px_0px_0px] transition-all",
        destructive:
          "bg-red-500 text-plantista-50 hover:bg-red-500/90 dark:bg-red-900 dark:text-plantista-50 dark:hover:bg-red-900/90",
        outline:
          "border-2 border-plantista-300 bg-transparent hover:bg-plantista-100 text-plantista-900 dark:border-plantista-700 dark:text-plantista-100 dark:hover:bg-plantista-900",
        secondary:
          "bg-plantista-950 text-plantista-50 hover:bg-plantista-800 dark:bg-plantista-50 dark:text-plantista-950 dark:hover:bg-plantista-200",
        ghost:
          "hover:bg-plantista-100 hover:text-plantista-900 dark:hover:bg-plantista-800 dark:hover:text-plantista-50",
        link: "text-plantista-900 underline-offset-4 hover:underline dark:text-plantista-50",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
